import type { Camera, Scene, Vector3 } from '@babylonjs/core';
import type { VisualSettings } from '../engine/Settings.js';
import { ChaseCamera } from './ChaseCamera';
import { TopDownCamera } from './TopDownCamera';

export type CameraMode = 'chase' | 'topdown';

const CYCLE_ORDER: CameraMode[] = ['chase', 'topdown'];

export class CameraController {
  private readonly chase: ChaseCamera;
  private readonly topdown: TopDownCamera;
  private readonly scene: Scene;
  private mode: CameraMode = 'chase';

  constructor(scene: Scene, settings: VisualSettings) {
    this.scene = scene;
    this.chase = new ChaseCamera(scene, settings);
    this.topdown = new TopDownCamera(scene);
    const engine = scene.getEngine();
    const resize = (): void => {
      this.topdown.resize(engine.getRenderWidth(), engine.getRenderHeight());
    };
    resize();
    engine.onResizeObservable.add(resize);
    this.scene.activeCamera = this.chase.camera;
  }

  activeCamera(): Camera {
    return this.mode === 'chase' ? this.chase.camera : this.topdown.camera;
  }

  /** The chase camera regardless of active mode — Sky / PostFx are bound to this one so
   *  they need a stable reference even while a top-down view is active. */
  chaseCamera(): Camera {
    return this.chase.camera;
  }

  getMode(): CameraMode {
    return this.mode;
  }

  cycle(): void {
    const i = CYCLE_ORDER.indexOf(this.mode);
    this.mode = CYCLE_ORDER[(i + 1) % CYCLE_ORDER.length]!;
    this.scene.activeCamera = this.activeCamera();
  }

  update(targetPos: Vector3, forward: Vector3, speed: number, dt: number): void {
    if (this.mode === 'chase') {
      this.chase.update(targetPos, forward, speed, dt);
    } else {
      this.topdown.update(targetPos, forward);
    }
  }

  snap(targetPos: Vector3, forward: Vector3): void {
    this.chase.snap(targetPos, forward);
    this.topdown.snap(targetPos, forward);
  }

  // ---- ChaseCamera passthroughs (used by main.ts in the per-frame loop / settings) ----

  triggerShake(magnitude: number): void {
    this.chase.triggerShake(magnitude);
  }

  syncBaselineDistance(distance: number): void {
    this.chase.syncBaselineDistance(distance);
  }

  // ---- TopDownCamera passthroughs (used by the teleport cinematic) ----

  /** Drive the topdown camera's ortho-extent zoom (1 = normal, larger = zoomed out). */
  setTopDownZoom(factor: number): void {
    this.topdown.setZoom(factor);
  }

  getTopDownZoom(): number {
    return this.topdown.getZoom();
  }
}
